import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Car, Layers, MessageSquareText, RectangleHorizontal, SquarePen, Type, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui";
import { cn } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/lib/api-client";
import { useScrollOnViewportResize } from "@/lib/useScrollOnViewportResize";
import { INSUFFICIENT_CREDITS } from "@/features/billing/utils";
import { InsufficientCreditsNotice } from "@/features/billing/components/InsufficientCreditsNotice";
import { useQuoteStore } from "../store";
import { useSendQuote } from "../hooks";
import { ChatBubble, TypingBubble } from "../components/ChatBubble";
import { Composer } from "../components/Composer";
import { QuoteCard } from "../components/QuoteCard";

/** 示例询价 (取自旧版 index.html 的示例; 点击填入输入框, 不直接发送) */
const EXAMPLES: Array<{ title: string; text: string; icon: LucideIcon }> = [
  { title: "KT 板 · 带包边", text: "KT板做1.2米宽50厘米长，要2张，带包边", icon: RectangleHorizontal },
  { title: "多个规格一起报", text: "70x 70 8张 单面 50x80 2张双面", icon: Layers },
  { title: "车贴", text: "打印一张30×40的车贴", icon: Car },
  { title: "立体字", text: "PVC 立体字 最长边 60cm, 厚度 2cm, 5 个", icon: Type },
];

export function QuotePage() {
  const archivedTurns = useQuoteStore((s) => s.archivedTurns);
  const currentTurns = useQuoteStore((s) => s.turns);
  const pending = useQuoteStore((s) => s.pending);
  const closeSession = useQuoteStore((s) => s.closeSession);
  // 屏幕展示 = 已闭合轮次 + 当前轮; 报价卡片挂在各轮的 assistant 消息上
  const turns = [...archivedTurns, ...currentTurns];
  const sendMutation = useSendQuote();
  const qc = useQueryClient();

  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  useScrollOnViewportResize(scrollRef);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [archivedTurns, currentTurns, error]);

  const handleSend = async (text: string) => {
    setError(null);
    try {
      const resp = await sendMutation.mutateAsync(text);
      // 计费模式下报价会扣余额, 刷新侧边栏余额
      void qc.invalidateQueries({ queryKey: ["me"] });
      if (resp.error && (!resp.success || !resp.needs_clarification)) {
        setError(resp.error);
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 402) {
        setError(INSUFFICIENT_CREDITS);
      } else if (e instanceof ApiError) {
        setError(`服务暂时不可用 (${e.status})`);
      } else if (e instanceof DOMException && e.name === "AbortError") {
        setError("请求超时, 请稍后重试");
      } else {
        setError("网络错误, 请稍后重试");
      }
    }
  };

  // 新对话: 只在本地换新会话, 不删除服务端记录 (与旧版一致, 问答记录里仍可查看)
  const handleNewChat = () => {
    setError(null);
    closeSession();
  };

  const fillExample = (ex: string) => {
    setDraft(ex);
    inputRef.current?.focus();
  };

  const isEmpty = turns.length === 0;

  return (
    <div className="flex h-full flex-col">
      <h1 className="sr-only">智能报价</h1>
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-3xl space-y-4 px-3 py-4 md:space-y-5 md:px-4 md:py-8">
          {isEmpty && <Welcome pending={pending} onPick={fillExample} />}

          {turns.map((t, idx) => (
            <ChatBubble
              key={`${t.seq}-${idx}`}
              role={t.role}
              content={t.content}
              pending={t.seq === -1}
            >
              {t.quote && (
                <QuoteCard
                  details={t.quote.details}
                  totalPrice={t.quote.totalPrice}
                  extractedParams={t.quote.extractedParams}
                  time={t.ts}
                />
              )}
            </ChatBubble>
          ))}

          {pending && <TypingBubble label="正在报价" />}

          {error === INSUFFICIENT_CREDITS ? (
            <InsufficientCreditsNotice />
          ) : (
            error && <ChatBubble role="assistant" tone="danger" content={error} />
          )}
        </div>
      </div>

      <Composer
        pending={pending}
        onSend={handleSend}
        value={draft}
        onChange={setDraft}
        inputRef={inputRef}
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={handleNewChat}
            disabled={pending || isEmpty}
            icon={<SquarePen />}
            className="-ml-2 text-muted-foreground hover:text-foreground"
            title="清屏开始新对话 (历史仍可在「记录」里查看)"
          >
            新对话
          </Button>
        }
      />
    </div>
  );
}

/** 空状态: 欢迎语 + 4 个示例卡片 (点击填入输入框, 不直接发送) */
function Welcome({ pending, onPick }: { pending: boolean; onPick: (text: string) => void }) {
  return (
    <div className="flex flex-col items-center px-1 pt-4 text-center md:pt-10">
      <span aria-hidden className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-brand to-primary text-white shadow-[0_6px_16px_rgba(76,175,80,0.35)] md:h-14 md:w-14">
        <MessageSquareText className="h-6 w-6 md:h-7 md:w-7" />
      </span>
      <h2 className="mt-4 text-xl font-semibold tracking-tight text-foreground md:text-2xl">您好，我是智能报价助手</h2>
      <p className="mt-1.5 max-w-md text-sm text-muted-foreground">
        用一句话描述产品的材质、尺寸和数量, 我会自动识别参数并算出价格。
      </p>
      <div className="mt-6 grid w-full gap-2.5 text-left sm:grid-cols-2 md:mt-8 md:gap-3">
        {EXAMPLES.map((ex) => (
          <button
            key={ex.text}
            type="button"
            onClick={() => onPick(ex.text)}
            disabled={pending}
            className={cn(
              "group flex items-start gap-3 rounded-xl border bg-card p-3.5 text-left shadow-sm transition-colors md:p-4",
              "hover:-translate-y-0.5 hover:border-brand hover:shadow-[0_4px_12px_rgba(76,175,80,0.18)]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50",
            )}
          >
            <span aria-hidden className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
              <ex.icon className="h-[18px] w-[18px]" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-foreground">{ex.title}</span>
              <span className="mt-0.5 block text-caption leading-5 text-muted-foreground">{ex.text}</span>
            </span>
            <ArrowUpRight aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
          </button>
        ))}
      </div>
    </div>
  );
}
